<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯員工帳號' : '新增員工帳號' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="loading">
          {{ isEdit ? '更新帳號' : '建立帳號' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-account-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="active-switch" :class="{ on: form.status === 'active' }">
          <div class="active-switch-copy">
            <span class="active-switch-title">狀態</span>
            <span class="active-switch-status">
              {{ form.status === 'active' ? '已啟用，可以登入後台' : '已停用，無法登入後台' }}
            </span>
          </div>
          <label class="active-toggle">
            <input
              type="checkbox"
              :checked="form.status === 'active'"
              aria-label="狀態"
              @change="form.status = $event.target.checked ? 'active' : 'inactive'"
            />
            <span class="active-toggle-slider"></span>
          </label>
        </div>
        <div v-if="errors.status" class="invalid-feedback active-switch-error">{{ errors.status }}</div>

        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          <div class="form-row">
            <div class="form-group">
              <label for="username" class="required">登入帳號</label>
              <input
                id="username"
                v-model="form.username"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.username }"
                placeholder="請輸入登入帳號"
                :disabled="isEdit"
                autocomplete="username"
                required
              />
              <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
              <p class="form-hint" v-if="isEdit">登入帳號建立後不可變更</p>
            </div>
            <div class="form-group">
              <label for="name" class="required">姓名</label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                placeholder="請輸入姓名"
                required
              />
              <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email" class="required">Email</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                placeholder="name@example.com"
                required
                autocomplete="email"
              />
              <div v-if="errors.email" class="invalid-feedback">{{ errors.email }}</div>
            </div>
            <div class="form-group">
              <label for="role">角色</label>
              <select id="role" v-model="form.role" class="form-control">
                <option value="client_staff">員工</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">轉派通知</h3>
          <p class="field-hint">客服不在線上時，工作台會依設定用這些資料通知。Email 使用上方的帳號 Email。</p>
          <div class="form-group">
            <label>LINE 好友</label>
            <LineFriendPicker v-model="form.notifyLineUserId" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="notifyPhone">簡訊手機</label>
              <input
                id="notifyPhone"
                v-model="form.notifyPhone"
                type="tel"
                class="form-control"
                :class="{ 'is-invalid': errors.notifyPhone }"
                placeholder="09 開頭的手機號碼"
                autocomplete="tel"
              />
              <div v-if="errors.notifyPhone" class="invalid-feedback">{{ errors.notifyPhone }}</div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">登入設定</h3>
          <div class="form-row">
            <div class="form-group">
              <label for="password" :class="!isEdit ? 'required' : ''">密碼</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.password }"
                :placeholder="isEdit ? '留空則不變更' : '請輸入密碼（至少 6 碼）'"
                :required="!isEdit"
                autocomplete="new-password"
                :minlength="isEdit ? 0 : 6"
              />
              <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
              <p class="form-hint" v-if="isEdit">留空表示不變更密碼</p>
              <p class="form-hint" v-else>密碼至少 6 碼</p>
            </div>
            <div class="form-group">
              <label for="confirmPassword" :class="!isEdit ? 'required' : ''">確認密碼</label>
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.confirmPassword }"
                :placeholder="isEdit ? '留空則不變更' : '再次輸入密碼'"
                :required="!isEdit"
                autocomplete="new-password"
              />
              <div v-if="errors.confirmPassword" class="invalid-feedback">
                {{ errors.confirmPassword }}
              </div>
              <p class="form-hint" v-if="!isEdit">請再次輸入密碼以確認</p>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">權限</h3>
          <div class="form-group">
            <label>權限</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.hasAllPermissions"
                />
                擁有全部權限
              </label>
            </div>
            <p class="form-hint">取消勾選後可限制可使用的模組代碼（以逗號分隔）</p>
          </div>

          <div class="form-group">
            <label>門市使用範圍</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="hasAllStores"
                />
                可使用全部門市
              </label>
            </div>
            <p class="form-hint">取消勾選後可限定可登入的門市</p>
          </div>

          <div class="form-group" v-if="!hasAllStores">
            <label for="storeSelect">選擇可使用的門市</label>
            <select
              id="storeSelect"
              v-model="selectedStoreIds"
              multiple
              class="form-control"
              :class="{ 'is-invalid': errors.allowedStores }"
            >
              <option
                v-for="store in stores"
                :key="store.id"
                :value="store.id"
              >
                {{ store.storeName || store.storeCode || '未命名' }}
              </option>
            </select>
            <p class="form-hint">
              按住 Ctrl/Command 可多選。{{ loadingStores ? '載入門市中...' : '' }}
            </p>
            <div v-if="errors.allowedStores" class="invalid-feedback">{{ errors.allowedStores }}</div>
          </div>
        </div>

        <!-- 程式功能設定區塊 -->
        <div class="form-section" v-if="!form.hasAllPermissions">
          <div class="section-header">
            <h3 class="section-title">程式功能設定</h3>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              @click="handleOpenAddProgramsDialog"
              :disabled="loadingPrograms"
            >
              <i class="ri-add-line"></i>
              添加程式
            </button>
          </div>

          <p class="form-hint" style="margin-bottom: 12px">
            為員工帳號設定可使用的程式功能。若「擁有全部權限」已勾選，則不需設定。{{ isEdit ? '' : '（程式將在帳號建立後立即設定）' }}
          </p>

          <div v-if="loadingPrograms" class="loading-message">
            <i class="ri-loader-4-line rotate"></i>
            載入程式列表中...
          </div>

          <div v-else-if="associatedPrograms.length === 0" class="empty-message">
            <i class="ri-information-line"></i>
            尚未設定任何程式功能，請點擊「添加程式」按鈕
          </div>

          <div v-else class="programs-list">
            <div
              v-for="program in associatedPrograms"
              :key="program.id"
              class="program-item"
            >
              <div class="program-info">
                <i :class="program.icon || 'ri-function-line'" class="program-icon"></i>
                <div class="program-details">
                  <div class="program-name">{{ program.programName }}</div>
                  <div class="program-permissions">
                    <span v-if="!program.allowedPermissions || program.allowedPermissions.length === 0">
                      全部權限
                    </span>
                    <span v-else class="permission-tags">
                      <span
                        v-for="perm in program.allowedPermissions"
                        :key="perm"
                        class="permission-tag"
                      >
                        {{ perm }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="program-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-link"
                  @click="openEditPermissionsDialog(program)"
                  :disabled="removingProgramId === program.id"
                  title="編輯權限"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger"
                  @click="handleRemoveProgram(program)"
                  :disabled="removingProgramId === program.id"
                  title="移除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
      </form>
    </div>

    <!-- 添加程式對話框 -->
    <ProgramSelector
      :show="showAddProgramsDialog"
      title="添加程式功能"
      confirm-button-text="添加"
      :available-programs="availablePrograms"
      :loading="loadingAvailablePrograms"
      :saving="addingPrograms"
      :initial-selected="{}"
      @close="showAddProgramsDialog = false"
      @confirm="handleAddPrograms"
    />

    <!-- 編輯權限對話框 -->
    <ProgramSelector
      :show="showEditPermissionsDialog"
      title="編輯程式權限"
      confirm-button-text="更新"
      :available-programs="editingProgram ? [editingProgram] : []"
      :loading="false"
      :saving="updatingPermissions"
      :initial-selected="editPermissionsInitialSelected"
      :edit-mode="true"
      @close="closeEditPermissionsDialog"
      @confirm="handleUpdatePermissions"
    />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { staffAccountService } from '@/api/modules/staffAccounts.js';
import { storeService } from '@/api/modules/stores.js';
import { accountProgramService } from '@/api/modules/accountPrograms.js';
import ProgramSelector from '@/components/ProgramSelector.vue';
import LineFriendPicker from '@/components/LineFriendPicker.vue';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'CreateStaffAccount',
  components: {
    ProgramSelector,
    LineFriendPicker
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const errors = reactive({});
    const stores = ref([]);
    const loadingStores = ref(false);
    const selectedStoreIds = ref([]);
    const hasAllStores = ref(true);

    // 程式功能管理相關
    const loadingPrograms = ref(false);
    const associatedPrograms = ref([]);
    const showAddProgramsDialog = ref(false);
    const loadingAvailablePrograms = ref(false);
    const availablePrograms = ref([]);
    const addingPrograms = ref(false);
    const removingProgramId = ref(null);
    
    // 編輯權限相關
    const showEditPermissionsDialog = ref(false);
    const editingProgram = ref(null);
    const editPermissionsSelected = ref([]);
    const updatingPermissions = ref(false);

    const isEdit = computed(() => !!route.params.id);

    // 編輯權限對話框的初始選中資料
    const editPermissionsInitialSelected = computed(() => {
      if (!editingProgram.value) return {};
      return {
        [editingProgram.value.id]: editingProgram.value.allowedPermissions || []
      };
    });

    const form = reactive({
      username: '',
      email: '',
      name: '',
      notifyLineUserId: '',
      notifyPhone: '',
      password: '',
      confirmPassword: '',
      role: 'client_staff',
      status: 'active',
      hasAllPermissions: true,
    });

    const validateForm = () => {
      errors.username = '';
      errors.email = '';
      errors.name = '';
      errors.notifyPhone = '';
      errors.password = '';
      errors.confirmPassword = '';
      errors.status = '';
      errors.allowedStores = '';

      if (!isEdit.value && !form.username.trim()) {
        errors.username = '請輸入登入帳號';
      }

      if (!form.name.trim()) {
        errors.name = '請輸入姓名';
      }

      if (!form.email.trim()) {
        errors.email = '請輸入 Email';
      }

      const phoneDigits = form.notifyPhone.replace(/\D/g, '');
      const normalizedPhone = phoneDigits.startsWith('886')
        ? `0${phoneDigits.slice(3)}`
        : (phoneDigits.length === 9 && phoneDigits.startsWith('9') ? `0${phoneDigits}` : phoneDigits);
      if (form.notifyPhone.trim() && !/^09\d{8}$/.test(normalizedPhone)) {
        errors.notifyPhone = '簡訊手機需為 09 開頭的手機號碼';
      }

      if (!isEdit.value && !form.password) {
        errors.password = '請輸入密碼';
      }

      if (!isEdit.value && !form.confirmPassword) {
        errors.confirmPassword = '請再次輸入密碼';
      }

      if (form.password || form.confirmPassword) {
        if (form.password.length < 6) {
          errors.password = '密碼至少 6 碼';
        }
        if (form.password !== form.confirmPassword) {
          errors.confirmPassword = '兩次輸入的密碼不一致';
        }
      }

      if (!form.status) {
        errors.status = '請選擇狀態';
      }

      if (!hasAllStores.value && selectedStoreIds.value.length === 0) {
        errors.allowedStores = '請至少選擇一個門市，或勾選全部門市';
      }

      return !errors.username && !errors.email && !errors.name && !errors.notifyPhone && !errors.password && !errors.confirmPassword && !errors.status && !errors.allowedStores;
    };

    const fetchStores = async () => {
      loadingStores.value = true;
      try {
        const allStores = [];
        let page = 1;
        const pageSize = 100; // API 限制 pageSize <= 100
        let hasMore = true;

        while (hasMore) {
          const response = await storeService.getStores({ page, pageSize, status: 'active' });
          if (response && response.success && response.data) {
            const items = response.data.items || [];
            allStores.push(...items);
            const total = response.data.pagination?.totalItems || 0;
            const fetched = page * pageSize;
            hasMore = fetched < total && items.length > 0;
            page += 1;
          } else {
            hasMore = false;
          }
        }

        stores.value = allStores;
      } catch (err) {
        console.error('載入門市失敗:', err);
      } finally {
        loadingStores.value = false;
      }
    };

    const loadAccount = async () => {
      if (!isEdit.value) return;
      loading.value = true;
      errorMessage.value = '';
      try {
        const response = await staffAccountService.getAccount(route.params.id);
        if (response && response.success && response.data) {
          form.username = response.data.username || '';
          form.email = response.data.email || '';
          form.name = response.data.name || '';
          form.notifyLineUserId = response.data.notifyLineUserId || '';
          form.notifyPhone = response.data.notifyPhone || '';
          form.role = response.data.role === 'staff' ? 'client_staff' : (response.data.role || 'client_staff');
          form.status = response.data.status || 'active';
          form.hasAllPermissions = response.data.hasAllPermissions !== false;
          if (response.data.allowedStores === null || response.data.allowedStores === undefined) {
            hasAllStores.value = true;
            selectedStoreIds.value = [];
          } else {
            hasAllStores.value = false;
            selectedStoreIds.value = response.data.allowedStores || [];
          }
        } else {
          const msg = (response && response.message) || '取得帳號資料失敗';
          errorMessage.value = msg;
          await showError('載入失敗', msg);
        }
      } catch (err) {
        console.error('取得員工帳號資料失敗:', err);
        const msg = err.message || '取得帳號資料失敗';
        errorMessage.value = msg;
        await showError('載入失敗', msg);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const accountData = {
          email: form.email.trim(),
          name: form.name.trim(),
          notifyLineUserId: form.notifyLineUserId.trim(),
          notifyPhone: form.notifyPhone.trim(),
          role: form.role,
          status: form.status,
          hasAllPermissions: form.hasAllPermissions,
        };

        if (!hasAllStores.value) {
          accountData.allowedStores = selectedStoreIds.value;
        }

        if (isEdit.value) {
          if (form.password) {
            accountData.password = form.password;
          }
          const response = await staffAccountService.updateAccount(route.params.id, accountData);
          if (response && response.success) {
            await showSuccess('更新成功', `員工帳號「${form.username}」已更新`);
            router.push('/client/staff/accounts');
          } else {
            const errorMsg = (response && response.message) || '更新帳號失敗';
            errorMessage.value = errorMsg;
            await showError('更新失敗', errorMsg);
          }
        } else {
          // 新增模式
          accountData.username = form.username.trim();
          accountData.password = form.password;
          const response = await staffAccountService.createAccount(accountData);
          if (response && response.success) {
            const newAccountId = response.data?.id;
            
            // 如果有選擇程式，且不是「擁有全部權限」，則批量添加程式
            if (newAccountId && !form.hasAllPermissions && associatedPrograms.value.length > 0) {
              try {
                const programIds = associatedPrograms.value.map(p => p.id);
                const programPermissions = {};
                associatedPrograms.value.forEach(p => {
                  if (p.allowedPermissions && p.allowedPermissions.length > 0) {
                    programPermissions[p.id] = p.allowedPermissions;
                  } else {
                    programPermissions[p.id] = null; // null 表示全部權限
                  }
                });
                
                await accountProgramService.addPrograms(
                  newAccountId,
                  programIds,
                  programPermissions,
                  'staff'
                );
              } catch (programError) {
                console.error('添加程式失敗:', programError);
                // 即使添加程式失敗，也不影響帳號創建成功的提示
                await showSuccess('新增成功', `員工帳號「${accountData.username}」已建立，但部分程式設定失敗，請到編輯頁面手動設定`);
                router.push('/client/staff/accounts');
                return;
              }
            }
            
            await showSuccess('新增成功', `員工帳號「${accountData.username}」已建立`);
            router.push('/client/staff/accounts');
          } else {
            const errorMsg = (response && response.message) || '新增帳號失敗';
            errorMessage.value = errorMsg;
            await showError('新增失敗', errorMsg);
          }
        }
      } catch (error) {
        console.error('提交員工帳號失敗:', error);
        const msg = error.message || '操作失敗，請稍後再試';
        errorMessage.value = msg;
        await showError(isEdit.value ? '更新失敗' : '新增失敗', msg);
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/staff/accounts');
    };

    // ==================== 程式功能管理函數 ====================

    // 載入帳號的程式列表
    const fetchAssociatedPrograms = async () => {
      if (!isEdit.value || !route.params.id) return;

      loadingPrograms.value = true;
      try {
        const response = await accountProgramService.getProgramsByAccountId(route.params.id, 'staff');
        if (response && response.success) {
          // 確保 permissions 是陣列格式
          const programs = (response.data || []).map(program => {
            let allowedPermissions = program.allowedPermissions;
            if (typeof allowedPermissions === 'string') {
              try {
                allowedPermissions = JSON.parse(allowedPermissions);
              } catch (e) {
                console.error('Failed to parse allowedPermissions for program:', program.id, e);
                allowedPermissions = [];
              }
            }
            if (!Array.isArray(allowedPermissions)) {
              allowedPermissions = [];
            }
            return {
              ...program,
              allowedPermissions
            };
          });
          associatedPrograms.value = programs;
        }
      } catch (error) {
        console.error('載入關聯程式失敗:', error);
      } finally {
        loadingPrograms.value = false;
      }
    };

    // 載入可選程式列表
    const fetchAvailablePrograms = async () => {
      loadingAvailablePrograms.value = true;
      try {
        // 編輯模式：通過 API 獲取可選程式
        if (isEdit.value && route.params.id) {
          const response = await accountProgramService.getAvailablePrograms(route.params.id, null, 'staff');
          if (response && response.success) {
            availablePrograms.value = response.data || [];
          }
        } else {
          // 新增模式：通過當前客戶的方案獲取可選程式
          const response = await accountProgramService.getAvailableProgramsForCreate('staff');
          if (response && response.success) {
            const allPrograms = response.data || [];
            // 過濾掉已選的程式
            const selectedIds = associatedPrograms.value.map(p => p.id);
            availablePrograms.value = allPrograms.filter(p => !selectedIds.includes(p.id));
          }
        }
      } catch (error) {
        console.error('載入可選程式失敗:', error);
        if (!isEdit.value) {
          await showError('載入失敗', error.message || '載入可選程式失敗，請確認您的客戶方案是否包含可用模組');
        } else {
          await showError('載入失敗', error.message || '載入可選程式失敗');
        }
        availablePrograms.value = [];
      } finally {
        loadingAvailablePrograms.value = false;
      }
    };

    // 打開添加程式對話框
    const handleOpenAddProgramsDialog = async () => {
      await fetchAvailablePrograms();
      showAddProgramsDialog.value = true;
    };

    // 添加程式到帳號
    const handleAddPrograms = async (data) => {
      addingPrograms.value = true;
      try {
        if (isEdit.value && route.params.id) {
          // 編輯模式：直接調用 API 添加
          const response = await accountProgramService.addPrograms(
            route.params.id,
            data.programIds,
            data.programPermissions,
            'staff'
          );

          if (response && response.success) {
            await showSuccess('添加成功', '程式已添加到帳號');
            showAddProgramsDialog.value = false;
            await fetchAssociatedPrograms(); // 重新載入關聯程式列表
          } else {
            await showError('添加失敗', response?.message || '添加程式失敗');
          }
        } else {
          // 新增模式：暫存到前端狀態，等帳號創建後再批量添加
          const newPrograms = data.programIds.map(programId => {
            const program = availablePrograms.value.find(p => p.id === programId);
            if (!program) return null;
            
            return {
              ...program,
              allowedPermissions: data.programPermissions && data.programPermissions[programId] 
                ? data.programPermissions[programId] 
                : null
            };
          }).filter(Boolean);
          
          associatedPrograms.value = [...associatedPrograms.value, ...newPrograms];
          showAddProgramsDialog.value = false;
          await showSuccess('添加成功', '程式已添加（將在帳號建立後生效）');
        }
      } catch (error) {
        console.error('添加程式失敗:', error);
        await showError('添加失敗', error.message || '添加程式失敗');
      } finally {
        addingPrograms.value = false;
      }
    };

    // 移除程式
    const handleRemoveProgram = async (program) => {
      const confirmed = await showDeleteConfirm(
        '確認移除',
        `確定要移除程式「${program.programName}」嗎？`
      );

      if (!confirmed) return;

      removingProgramId.value = program.id;
      try {
        if (isEdit.value && route.params.id) {
          // 編輯模式：調用 API 移除
          const response = await accountProgramService.removeProgram(
            route.params.id,
            program.id,
            'staff'
          );

          if (response && response.success) {
            await showSuccess('移除成功', `程式「${program.programName}」已移除`);
            await fetchAssociatedPrograms(); // 重新載入關聯程式列表
          } else {
            await showError('移除失敗', response?.message || '移除程式失敗');
          }
        } else {
          // 新增模式：從前端狀態移除
          associatedPrograms.value = associatedPrograms.value.filter(p => p.id !== program.id);
          await showSuccess('移除成功', `程式「${program.programName}」已移除`);
        }
      } catch (error) {
        console.error('移除程式失敗:', error);
        await showError('移除失敗', error.message || '移除程式失敗');
      } finally {
        removingProgramId.value = null;
      }
    };

    // 打開編輯權限對話框
    const openEditPermissionsDialog = (program) => {
      editingProgram.value = program;
      showEditPermissionsDialog.value = true;
    };

    // 關閉編輯權限對話框
    const closeEditPermissionsDialog = () => {
      showEditPermissionsDialog.value = false;
      editingProgram.value = null;
    };

    // 更新程式權限
    const handleUpdatePermissions = async (data) => {
      if (!editingProgram.value) return;

      updatingPermissions.value = true;
      try {
        const programId = editingProgram.value.id;
        const allowedPermissions = data.programPermissions[programId] || null;

        if (isEdit.value && route.params.id) {
          // 編輯模式：調用 API 更新
          const response = await accountProgramService.updateProgramPermissions(
            route.params.id,
            programId,
            allowedPermissions,
            'staff'
          );

          if (response && response.success) {
            await showSuccess('更新成功', '權限已更新');
            closeEditPermissionsDialog();
            await fetchAssociatedPrograms(); // 重新載入關聯程式列表
          } else {
            await showError('更新失敗', response?.message || '更新權限失敗');
          }
        } else {
          // 新增模式：更新前端狀態
          const programIndex = associatedPrograms.value.findIndex(p => p.id === programId);
          if (programIndex !== -1) {
            associatedPrograms.value[programIndex].allowedPermissions = allowedPermissions;
            await showSuccess('更新成功', '權限已更新');
            closeEditPermissionsDialog();
          }
        }
      } catch (error) {
        console.error('更新權限失敗:', error);
        await showError('更新失敗', error.message || '更新權限失敗');
      } finally {
        updatingPermissions.value = false;
      }
    };

    // 監聽 hasAllPermissions 變化
    watch(() => form.hasAllPermissions, async (newVal, oldVal) => {
      // 如果從取消勾選改為勾選，且有已設定程式，則清空程式
      if (newVal && !oldVal && associatedPrograms.value.length > 0) {
        const confirmed = await showDeleteConfirm(
          '確認清除程式功能設定',
          '勾選「擁有全部權限」後，已設定的程式功能將被清除。確定要繼續嗎？'
        );

        if (confirmed) {
          if (isEdit.value && route.params.id) {
            // 編輯模式：調用 API 清空程式
            try {
              for (const program of associatedPrograms.value) {
                try {
                  await accountProgramService.removeProgram(route.params.id, program.id, 'staff');
                } catch (error) {
                  console.error(`移除程式 ${program.id} 失敗:`, error);
                }
              }

              // 更新 hasAllPermissions 狀態
              await staffAccountService.updateAccount(route.params.id, {
                hasAllPermissions: true
              });

              await showSuccess('已更新', '權限狀態已更新為「擁有全部權限」');
              await fetchAssociatedPrograms();
            } catch (error) {
              console.error('更新權限狀態失敗:', error);
              await showError('更新失敗', '更新權限狀態失敗，請手動點擊「更新帳號」按鈕');
            }
          } else {
            // 新增模式：清空前端狀態
            associatedPrograms.value = [];
          }
        } else {
          // 用戶取消，恢復原狀
          form.hasAllPermissions = false;
        }
      }

      // 從勾選改為取消勾選時，載入可選程式（只在編輯模式下）
      if (!newVal && oldVal && isEdit.value) {
        await fetchAssociatedPrograms();
      }
    });

    // ==================== 結束程式功能管理函數 ====================

    onMounted(() => {
      loadAccount();
      fetchStores();
      if (isEdit.value) {
        fetchAssociatedPrograms();
      }
    });

    return {
      form,
      errors,
      loading,
      errorMessage,
      stores,
      loadingStores,
      selectedStoreIds,
      hasAllStores,
      isEdit,
      handleSubmit,
      handleCancel,
      // 程式功能管理
      loadingPrograms,
      associatedPrograms,
      showAddProgramsDialog,
      loadingAvailablePrograms,
      availablePrograms,
      addingPrograms,
      removingProgramId,
      showEditPermissionsDialog,
      editingProgram,
      editPermissionsInitialSelected,
      updatingPermissions,
      handleOpenAddProgramsDialog,
      handleAddPrograms,
      handleRemoveProgram,
      openEditPermissionsDialog,
      closeEditPermissionsDialog,
      handleUpdatePermissions,
    };
  },
};
</script>

<style scoped>
.create-account-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.active-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -24px -24px 20px;
  padding: 16px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 8px 8px 0 0;
}

.active-switch.on {
  background: #f0fdf4;
  border-bottom-color: #bbf7d0;
}

.active-switch-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.active-switch-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.active-switch-status {
  font-size: 13px;
  color: #6b7280;
}

.active-switch.on .active-switch-status {
  color: #15803d;
}

.active-switch-error {
  margin: -12px 0 16px;
}

.active-toggle {
  position: relative;
  display: inline-block;
  width: 56px;
  height: 32px;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
}

.active-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.active-toggle-slider {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 32px;
  transition: background 0.2s;
}

.active-toggle-slider::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  left: 4px;
  top: 4px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.active-toggle input:checked + .active-toggle-slider {
  background: #16a34a;
}

.active-toggle input:checked + .active-toggle-slider::before {
  transform: translateX(24px);
}

.active-toggle input:focus-visible + .active-toggle-slider {
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
}

.form-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
}

.form-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}

.field-hint {
  margin: -8px 0 16px;
  color: #666;
  font-size: 13px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 6px;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-group .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  font-weight: 500;
  line-height: 1.25;
}

.form-group .checkbox-label input {
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.form-error {
  margin-top: 12px;
  color: #dc3545;
  font-weight: 500;
}

/* 程式功能設定區塊樣式 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header .section-title {
  margin-bottom: 0;
}

.loading-message,
.empty-message {
  text-align: center;
  padding: 24px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-message i {
  font-size: 18px;
  animation: rotate 1s linear infinite;
}

.empty-message i {
  font-size: 18px;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.programs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.program-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.2s;
}

.program-item:hover {
  background: #f1f3f5;
  border-color: #dee2e6;
}

.program-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.program-icon {
  font-size: 24px;
  color: #667eea;
}

.program-details {
  flex: 1;
}

.program-name {
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
}

.program-permissions {
  font-size: 13px;
  color: #666;
}

.permission-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.permission-tag {
  display: inline-block;
  padding: 2px 8px;
  background: #e7f3ff;
  color: #0066cc;
  border-radius: 4px;
  font-size: 12px;
}

.program-actions {
  display: flex;
  gap: 8px;
}

.btn-link {
  padding: 6px 10px;
  color: #667eea;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
}

.btn-link:hover {
  color: #5568d3;
}

.btn-link.text-danger {
  color: #dc3545;
}

.btn-link.text-danger:hover {
  color: #bd2130;
}

.btn-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .program-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .program-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
